const splitRow = (line) =>
  line
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.replace(/\*/g, "").trim());

const isTotalLabel = (label) => /overall|health|total/i.test(label);
const stripMd = (s) => s.replace(/\*\*/g, "").replace(/`/g, "").replace(/:$/, "").trim();

/* ── 1. Score table: | Category | Score | Key Driver | ─────────── */
function parseScoreTable(md) {
  const lines = (md || "").split("\n").map((l) => l.trim());
  const head = lines.findIndex((l) => l.startsWith("|") && /score/i.test(l));
  if (head === -1) return [];

  const scoreCol = splitRow(lines[head]).findIndex((h) => /score/i.test(h));
  const rows = [];

  for (const l of lines.slice(head + 1)) {
    if (!l.startsWith("|")) break; // table ended
    if (/^\|[\s|:-]+\|?$/.test(l)) continue; // the |---|---| line
    const cells = splitRow(l);
    const value = cells[scoreCol]?.match(/\d{1,3}/)?.[0];
    if (!value) continue;
    rows.push({
      label: cells[0],
      value,
      driver: cells[scoreCol + 1] || "",
      unchecked: /not checked/i.test(cells[scoreCol]),
    });
  }
  return rows;
}

/* ── 2. Scores: table first, then "Label (NN/100)" lines ───────── */
export function parseScores(md) {
  const text = md || "";
  const scores = [];
  let overall = null;

  for (const r of parseScoreTable(text)) {
    if (isTotalLabel(r.label)) overall = overall || r.value;
    else scores.push(r);
  }
  if (scores.length) return { scores, overall };

  const re =
    /^[\s|*\-•#\d.]*\**([A-Za-z][A-Za-z &/+-]{1,40}?)\**\s*(?:score)?\s*[(:—–|-]+\s*\**\s*(\d{1,3})\s*\/\s*100/gim;
  for (const m of text.matchAll(re)) {
    const label = m[1].trim();
    if (isTotalLabel(label)) overall = overall || m[2];
    else if (!scores.some((s) => s.label === label))
      scores.push({ label, value: m[2], driver: "", unchecked: false });
  }
  return { scores, overall };
}

/* ── 3. Sections: any "## Title" or "**Title:**" that has a list ── */
export function parseSections(md) {
  const lines = (md || "").split("\n").map((l) => l.trim());
  const sections = [];
  let current = null;
  let inTable = false, skipTable = false, tableStart = 0;

  for (const t of lines) {
    // ── table rows ──
    if (t.startsWith("|")) {
      if (!current) continue;
      if (/^\|[\s|:-]+\|?$/.test(t)) {
        // the |---|---| line: the row before it was the header
        if (inTable) {
          const header = current.items[tableStart];
          current.items.splice(tableStart, 1);
          skipTable = /score/i.test(header); // score tables belong to Score breakdown
        }
        continue;
      }
      if (!inTable) {
        inTable = true;
        skipTable = false;
        tableStart = current.items.length;
        current.items.push(splitRow(t).join(" "));
      } else if (!skipTable) {
        let cells = splitRow(t).filter(Boolean);
        if (/^(\d+|#)$/.test(cells[0])) cells = cells.slice(1); // drop the "1", "2" number column
        if (cells.length) current.items.push(stripMd(cells.join(" — ")));
      }
      continue;
    }
    inTable = false;

    // ── headings / bold titles / list items (same as before) ──
    const heading = t.match(/^#{1,6}\s+(.*)/);
    const bold = t.match(/^\*\*([^*]+)\*\*:?$/);
    if (heading || bold) {
      current = { title: stripMd((heading || bold)[1]), items: [] };
      sections.push(current);
      continue;
    }
    const item = t.match(/^(?:[-*]|\d+\.)\s+(.*)/);
    if (item && current) current.items.push(stripMd(item[1]));
  }

  return sections.filter(
    (s) => s.items.length && !/score|breakdown|snapshot|checked/i.test(s.title),
  );
}

const PRIORITY = /priorit|action|quick win|next step|fix first/i;

// Action-style sections (Top 5 Actions, Quick Wins, Priorities...)
export const parsePriorities = (md) =>
  parseSections(md).filter((s) => PRIORITY.test(s.title));

// Everything else with a list (Key Findings, Critical gaps, What's working...)
export const parseFindings = (md) =>
  parseSections(md).filter((s) => !PRIORITY.test(s.title));